import { useState, useEffect, useCallback } from 'react';
import { Validator, validate as validateActual } from 'utils/validation';

interface ValidationResult {
  valid: boolean;
  errors: { [key: string]: string[] };
}

const useValidation = <T extends { [key: string]: unknown }>(
  schema: { [key in keyof T]: Validator },
  values?: T,
) => {
  const [result, setResult] = useState<ValidationResult>({ valid: true, errors: {} });
  const validate = useCallback(
    (currentValues?: T): boolean => {
      const validationResult: ValidationResult = validateActual(
        schema,
        currentValues || values || {},
      );
      setResult(validationResult);
      return validationResult.valid;
    },
    [schema, values],
  );

  useEffect(() => {
    if (values) {
      validate();
    }
  }, [values, validate]);

  const clearError = useCallback((key: string) => {
    setResult((previous) => {
      const temporary = previous;
      if (temporary.errors[key]) {
        delete temporary.errors[key];
      }
      return temporary;
    });
  }, []);

  return {
    errors: result.errors,
    valid: result.valid,
    validate,
    clearError,
  };
};

export default useValidation;
